/**
 * @fileoverview Composable for chat history download functionality
 * @module composables/useChatHistory
 *
 * Manages chat history export in various formats (text, PDF, email)
 * including email form state and validation.
 *
 * @vue3migration - Phase 3F: Extracted from ChatWindow/Index.vue
 */
import { ref, reactive, computed, type ComputedRef, type Ref } from 'vue'
import * as _ from 'lodash-es'
import dayjs from 'dayjs'
import saveAs from 'file-saver'

/**
 * Chat message interface for history export
 */
interface ChatMessage {
  id: string
  type: 'message' | 'reply' | 'postback' | 'agent'
  timestamp: Date | string
  data: {
    content: Array<{
      text?: string
      files?: any[]
      images?: any[]
    }>
  }
}

/**
 * Store interface for chat history operations
 */
interface HistoryStore {
  uid: string
  messages: ChatMessage[]
  settings: {
    eceActivityId?: string
    historyHideAgentName?: boolean
    historyDeliveryMode?: string
  }
  emailHistory: (form: { userEmail: string }) => Promise<any>
}

/**
 * Options for useChatHistory composable
 */
interface UseChatHistoryOptions {
  /** Pinia store instance */
  store: HistoryStore
  /** Whether user source exists in URL */
  userSourceExist: ComputedRef<boolean>
  /** Whether mobile mode is active */
  isMobileMode: ComputedRef<boolean>
  /** Whether iOS SDK is used */
  iOSSDK: boolean
}

/**
 * Email validator function type
 */
type ValidatorCallback = (error?: Error) => void

/**
 * Composable for chat history download functionality
 *
 * @param options - Dependencies and configuration
 * @returns History state and control methods
 *
 * @example
 * ```ts
 * const {
 *   emailFormVisible,
 *   form,
 *   emailForm,
 *   loading,
 *   generateHistoryText,
 *   downloadHistoryAsText,
 *   downloadHistoryAsPdf,
 * } = useChatHistory({
 *   store: useMainStore(),
 *   userSourceExist,
 *   isMobileMode,
 *   iOSSDK: false,
 * })
 * ```
 */
export function useChatHistory(options: UseChatHistoryOptions) {
  const { store, userSourceExist, isMobileMode, iOSSDK } = options

  // ====================
  // State
  // ====================

  /** Whether email form dialog is visible */
  const emailFormVisible = ref(false)

  /** Email form data */
  const form = reactive({
    userEmail: '',
  })

  /** Loading state for email submission */
  const loading = ref(false)

  // ====================
  // Email Validation
  // ====================

  /**
   * Create email validator function
   *
   * @param emailDomainWhitelist - List of allowed email domains
   * @returns Validator function for email field
   */
  function createEmailValidator(emailDomainWhitelist: string[] = []) {
    return (_rule: any, value: string, callback: ValidatorCallback) => {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      const validEmail = re.test(String(value).toLowerCase())

      if (value === '') {
        callback(new Error('Please input the email address.'))
        return
      } else if (!validEmail) {
        callback(new Error('Please input valid email address.'))
        return
      } else if (!_.isEmpty(emailDomainWhitelist)) {
        const userDomainIsIncludedInWhitelist = _.find(emailDomainWhitelist, (domain) =>
          value.includes(domain)
        )

        if (!userDomainIsIncludedInWhitelist) {
          callback(new Error('Receiver email is not in whitelist. Please contact admin.'))
          return
        }
      }

      callback()
    }
  }

  // ====================
  // History Generation
  // ====================

  /**
   * Generate chat history text content
   *
   * @returns Formatted chat history text
   */
  function generateHistoryText(): string {
    let idtext = `\r\nuid: ${store.uid}`
    const eceActivityId = store.settings.eceActivityId
    if (eceActivityId) {
      idtext = `\r\nactivityId: ${eceActivityId}`
    }

    let text = `---- User Info ----
      ${idtext}
			\r\ntimestamp_generated: ${dayjs().format('DD-MM-YYYY HH:mm:ss')}
			${!userSourceExist.value ? `\r\nurl: ${window.location.href.split('?')[0]}` : ''}
			\r\n
			\r\n---- Chat history ----
      \r\n`

    const chat = _.sortBy(store.messages, (message) => message.timestamp)
    _.each(chat, (message) => {
      text += dayjs(message.timestamp).format('DD-MM-YYYY HH:mm:ss')
      let prompt = ' bot: '
      if (message.type === 'message' || message.type === 'postback') {
        prompt = ' user: '
      } else if (message.type === 'agent') {
        prompt = ' agent: '
      }
      text += prompt
      text += _.map(message.data.content, (content) => {
        if (!_.isEmpty(content.text)) {
          return content.text
        } else if (!_.isEmpty(content.files)) {
          return '[File Content]'
        } else if (!_.isEmpty(content.images)) {
          return '[Image Content]'
        } else {
          return 'user connected'
        }
      }).join(' | ')
      text += '\r\n'
      // Optionally hide agent name
      if (store.settings.historyHideAgentName) {
        text = text.replace(/bot: (.+) has joined the chat\./, 'bot: Agent has joined the chat.')
      }
    })

    return text
  }

  /**
   * Download chat history as text file
   */
  function downloadHistoryAsText() {
    const text = generateHistoryText()
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    saveAs(blob, 'chat_history.txt')
  }

  /**
   * Download chat history as PDF file
   * Uses pdf-lib for PDF generation with custom fonts
   */
  async function downloadHistoryAsPdf() {
    const text = generateHistoryText()

    const [{ PDFDocument, StandardFonts, rgb }, fontkitModule] = await Promise.all([
      import('pdf-lib'),
      import('@pdf-lib/fontkit'),
    ])
    const fontkit = (fontkitModule as any).default ?? fontkitModule
    const pdfDoc = await PDFDocument.create()
    pdfDoc.registerFontkit(fontkit)

    let fontUrl = 'https://keyreply.blob.core.windows.net/files/fonts/NotoSans-Regular.ttf'
    const webchatUrl = _.get(window, 'appConfig.WEBCHAT_URL')
    if (webchatUrl) {
      fontUrl = new URL('fonts/NotoSans-Regular.ttf', webchatUrl as string).href
    }
    const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer())
    const font = await pdfDoc.embedFont(fontBytes)

    const x = 32
    const lineHeight = 8
    const maxCharsPerLine = 125

    let page = pdfDoc.addPage()
    let { width, height } = page.getSize()
    let y = height - 32

    const lines = text.split('\n')
    for (let i = 0; i < lines.length; i++) {
      let words = lines[i].split(' ')
      let line = ''

      while (words.length > 0) {
        if (y < lineHeight * 2) {
          // If there's not enough space for another line, add a new page
          page = pdfDoc.addPage()
          ;({ width, height } = page.getSize())
          y = height - 32
        }
        while (words.length > 0 && (line + words[0]).length < maxCharsPerLine) {
          line += words.shift() + ' '
        }
        page.drawText(line.trim(), { x, y, size: lineHeight, font, color: rgb(0, 0.53, 0.71) })
        y -= lineHeight
        if (line.trim() !== '') {
          y -= lineHeight // Subtract an extra lineHeight value when the line is not empty
        }
        line = ''
      }
    }

    const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true })
    saveAs(pdfDataUri, 'chat_history.pdf')
  }

  /**
   * Determine download action based on settings
   * Returns whether email form should be shown
   *
   * @returns True if email form should be shown
   */
  function shouldShowEmailForm(): boolean {
    const useEmailDelivery = _.get(store.settings, 'historyDeliveryMode') === 'EMAIL'
    return useEmailDelivery || iOSSDK || isMobileMode.value
  }

  /**
   * Show email form dialog
   */
  function showEmailForm() {
    emailFormVisible.value = true
  }

  /**
   * Hide email form dialog
   */
  function hideEmailForm() {
    emailFormVisible.value = false
  }

  /**
   * Reset form after submission
   */
  function resetForm() {
    form.userEmail = ''
  }

  return {
    // State
    emailFormVisible,
    form,
    loading,

    // Methods
    createEmailValidator,
    generateHistoryText,
    downloadHistoryAsText,
    downloadHistoryAsPdf,
    shouldShowEmailForm,
    showEmailForm,
    hideEmailForm,
    resetForm,
  }
}
